import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SlidersHorizontal, Loader2, AlertCircle } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { employeeService } from '../../empleados/services/employeeService';
import {
  formatCalendarOption,
  toDateInputValue,
} from '../utils/laborCalendarUtils';
import { getEmployeeDisplayName } from '../utils/laborFactoryUtils';
import { LaborCalendarModalShell } from './LaborCalendarModalShell';

const SCOPE = {
  FACTORY: 'factory',
  EMPLOYEE: 'employee',
};

export function LaborOverrideModal({
  isOpen,
  onClose,
  factoryId,
  factoryCode,
  defaultYear,
  defaultCalendarId = null,
  defaultEmployeeId = null,
  lockEmployeeScope = false,
  editingOverride = null,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const isEdit = Boolean(editingOverride?.id);

  const [calendarId, setCalendarId] = useState('');
  const [scope, setScope] = useState(SCOPE.FACTORY);
  const [employeeId, setEmployeeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isWorkingDay, setIsWorkingDay] = useState(true);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [useCustomHours, setUseCustomHours] = useState(false);
  const [error, setError] = useState(null);
  const prevIsOpenRef = useRef(false);

  const { data: calendars = [], isLoading: loadingCalendars } = useQuery({
    queryKey: ['labor-calendars', factoryId],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId),
    enabled: isOpen && Boolean(factoryId),
  });

  const { data: employees = [], isLoading: loadingEmployees } = useQuery({
    queryKey: ['labor-calendar-employees', factoryId],
    queryFn: () => employeeService.getEmployees('', true, factoryId),
    enabled: isOpen && Boolean(factoryId) && (scope === SCOPE.EMPLOYEE || lockEmployeeScope),
  });

  // Inicializar formulario solo al abrir el modal (crear o editar)
  useEffect(() => {
    if (!isOpen) {
      prevIsOpenRef.current = false;
      return;
    }

    const justOpened = !prevIsOpenRef.current;
    prevIsOpenRef.current = true;
    if (!justOpened) return;

    if (editingOverride) {
      const empId = editingOverride.employee_id;
      setScope(empId ? SCOPE.EMPLOYEE : SCOPE.FACTORY);
      setEmployeeId(
        empId
          ? String(empId)
          : lockEmployeeScope && defaultEmployeeId
            ? String(defaultEmployeeId)
            : ''
      );
      setCalendarId(String(editingOverride.calendar_id || defaultCalendarId || ''));
      setStartDate(toDateInputValue(editingOverride.start_date));
      setEndDate(toDateInputValue(editingOverride.end_date));
      setIsWorkingDay(editingOverride.is_working_day !== false);
      setStartTime(editingOverride.start_time || '');
      setEndTime(editingOverride.end_time || '');
      setUseCustomHours(Boolean(editingOverride.start_time && editingOverride.end_time));
      setReason(editingOverride.reason || '');
      setError(null);
      return;
    }

    const defaultDate = `${defaultYear}-07-16`;
    setScope(lockEmployeeScope || defaultEmployeeId ? SCOPE.EMPLOYEE : SCOPE.FACTORY);
    setEmployeeId(defaultEmployeeId ? String(defaultEmployeeId) : '');
    setStartDate(defaultDate);
    setEndDate(defaultDate);
    setIsWorkingDay(true);
    setStartTime('10:00');
    setEndTime('14:00');
    setUseCustomHours(false);
    setReason('');
    setError(null);
  }, [
    isOpen,
    editingOverride,
    defaultYear,
    defaultCalendarId,
    defaultEmployeeId,
    lockEmployeeScope,
  ]);

  // Asignar calendario al abrir o cuando carguen (igual que LaborAddHolidayModal)
  useEffect(() => {
    if (!isOpen || isEdit) return;
    if (defaultCalendarId) {
      setCalendarId(String(defaultCalendarId));
      return;
    }
    if (calendars.length === 0) return;
    const match = calendars.find((c) => Number(c.year) === Number(defaultYear));
    setCalendarId(String((match || calendars[0]).id));
  }, [isOpen, isEdit, calendars, defaultYear, defaultCalendarId]);

  const effectiveCalendarId = useMemo(() => {
    if (calendarId) return calendarId;
    if (defaultCalendarId != null && defaultCalendarId !== '') {
      return String(defaultCalendarId);
    }
    if (calendars.length === 0) return '';
    const match = calendars.find((c) => Number(c.year) === Number(defaultYear));
    return String((match || calendars[0]).id);
  }, [calendarId, defaultCalendarId, calendars, defaultYear]);

  const mutation = useMutation({
    mutationFn: () => {
      const calId = calendarId || effectiveCalendarId;
      if (!calId) throw new Error('Selecciona un calendario de planta.');
      if (!startDate || !endDate) throw new Error('Indica fecha de inicio y fin.');
      if (startDate > endDate) throw new Error('La fecha de inicio debe ser anterior o igual a la de fin.');
      if (!reason.trim()) throw new Error('El motivo es obligatorio.');
      if (scope === SCOPE.EMPLOYEE && !employeeId && !lockEmployeeScope) {
        throw new Error('Selecciona un empleado para el override.');
      }
      if (lockEmployeeScope && !employeeId && !defaultEmployeeId) {
        throw new Error('No hay empleado seleccionado.');
      }

      const payload = {
        employeeId: scope === SCOPE.EMPLOYEE ? (employeeId || defaultEmployeeId) : null,
        startDate,
        endDate,
        isWorkingDay,
        startTime: useCustomHours && startTime ? startTime : null,
        endTime: useCustomHours && endTime ? endTime : null,
        reason: reason.trim(),
      };

      if (isEdit) {
        return laborScheduleService.updateOverride(editingOverride.id, payload);
      }
      return laborScheduleService.createOverride(calId, payload);
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['labor-overrides'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
      onSuccess?.(data);
      onClose();
    },
    onError: (err) => {
      setError(err?.message || 'No se pudo guardar el override');
    },
  });

  const lockedEmployeeName = useMemo(() => {
    if (!lockEmployeeScope || !defaultEmployeeId) return null;
    const emp = employees.find((e) => String(e.id) === String(defaultEmployeeId));
    return getEmployeeDisplayName(emp) || `ID ${defaultEmployeeId}`;
  }, [lockEmployeeScope, defaultEmployeeId, employees]);

  const needsEmployeeSelection =
    scope === SCOPE.EMPLOYEE && !lockEmployeeScope && !employeeId;

  const submitDisabled =
    mutation.isPending ||
    (loadingCalendars && !effectiveCalendarId) ||
    !effectiveCalendarId ||
    needsEmployeeSelection;

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={mutation.isPending}
        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mutation.mutate();
        }}
        disabled={submitDisabled}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium disabled:opacity-50"
      >
        {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {isEdit ? 'Guardar cambios' : 'Crear override'}
      </button>
    </>
  );

  return (
    <LaborCalendarModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar override' : 'Crear override'}
      icon={SlidersHorizontal}
      footer={footer}
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <p className="text-sm text-gray-400 mb-4">
        Fábrica <span className="text-white font-medium">{factoryCode}</span>.
        Los overrides de empleado tienen prioridad sobre los de fábrica.
      </p>

      <label htmlFor="labor-ov-cal" className="block text-sm text-gray-300 mb-2">
        Calendario <span className="text-red-400">*</span>
      </label>
      {loadingCalendars ? (
        <p className="text-gray-500 text-sm mb-4">Cargando calendarios…</p>
      ) : calendars.length === 0 ? (
        <p className="text-amber-400 text-sm mb-4 bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
          No hay calendarios. Crea uno en la vista Fábrica primero.
        </p>
      ) : (
        <select
          id="labor-ov-cal"
          value={effectiveCalendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          disabled={mutation.isPending || isEdit}
          className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm mb-4"
        >
          {calendars.map((cal) => (
            <option key={cal.id} value={cal.id}>
              {formatCalendarOption(cal)}
            </option>
          ))}
        </select>
      )}

      {!lockEmployeeScope && (
        <>
          <label className="block text-sm text-gray-300 mb-2">Ámbito</label>
          <div className="flex rounded-lg overflow-hidden border border-gray-600 mb-4">
            <button
              type="button"
              onClick={() => setScope(SCOPE.FACTORY)}
              disabled={mutation.isPending}
              className={`flex-1 px-3 py-2 text-sm ${
                scope === SCOPE.FACTORY
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-900 text-gray-300 hover:bg-gray-800'
              }`}
            >
              Toda la fábrica
            </button>
            <button
              type="button"
              onClick={() => setScope(SCOPE.EMPLOYEE)}
              disabled={mutation.isPending}
              className={`flex-1 px-3 py-2 text-sm ${
                scope === SCOPE.EMPLOYEE
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-900 text-gray-300 hover:bg-gray-800'
              }`}
            >
              Empleado concreto
            </button>
          </div>
        </>
      )}

      {scope === SCOPE.EMPLOYEE && (
        <div className="mb-4">
          <label htmlFor="labor-ov-emp" className="block text-sm text-gray-300 mb-2">
            Empleado <span className="text-red-400">*</span>
          </label>
          {lockEmployeeScope ? (
            <p className="text-sm text-white bg-gray-900 border border-gray-600 rounded-lg px-3 py-2">
              {lockedEmployeeName || `Empleado ID ${defaultEmployeeId}`}
            </p>
          ) : loadingEmployees ? (
            <p className="text-gray-500 text-sm">Cargando empleados…</p>
          ) : (
            <select
              id="labor-ov-emp"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              disabled={mutation.isPending}
              className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Seleccionar empleado…</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {getEmployeeDisplayName(emp)}
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label htmlFor="labor-ov-start" className="block text-sm text-gray-300 mb-2">
            Inicio <span className="text-red-400">*</span>
          </label>
          <input
            id="labor-ov-start"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={mutation.isPending}
            className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="labor-ov-end" className="block text-sm text-gray-300 mb-2">
            Fin <span className="text-red-400">*</span>
          </label>
          <input
            id="labor-ov-end"
            type="date"
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => setEndDate(e.target.value)}
            disabled={mutation.isPending}
            className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer mb-4">
        <input
          type="checkbox"
          checked={isWorkingDay}
          onChange={(e) => setIsWorkingDay(e.target.checked)}
          disabled={mutation.isPending}
          className="rounded border-gray-600"
        />
        Día laborable (desmarcar = no laborable / no trabaja)
      </label>

      <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer mb-3">
        <input
          type="checkbox"
          checked={useCustomHours}
          onChange={(e) => setUseCustomHours(e.target.checked)}
          disabled={mutation.isPending}
          className="rounded border-gray-600"
        />
        Horario alternativo (sustituye al del patrón)
      </label>

      {useCustomHours && (
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label htmlFor="labor-ov-st" className="block text-xs text-gray-400 mb-1">
              Hora inicio
            </label>
            <input
              id="labor-ov-st"
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              disabled={mutation.isPending}
              className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="labor-ov-et" className="block text-xs text-gray-400 mb-1">
              Hora fin
            </label>
            <input
              id="labor-ov-et"
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              disabled={mutation.isPending}
              className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}

      <label htmlFor="labor-ov-reason" className="block text-sm text-gray-300 mb-2">
        Motivo <span className="text-red-400">*</span>
      </label>
      <textarea
        id="labor-ov-reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={mutation.isPending}
        rows={3}
        placeholder="Ej. Jornada especial por calor extremo"
        className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm resize-y"
      />
    </LaborCalendarModalShell>
  );
}
